import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getLocale, shallowEqual, useSelector } from 'umi';
import { EntryProps } from '../interface';
import { Select } from 'tea-component';
import { checkNullString } from '@/pages/datalab/utils/utils';
import { FilterStringOptions } from '@dbl/core/filter';
import type { OptionsItemType } from '@home/components/NewRegionSelect/type';
import CascadeFilterComponent from '@/pages/dashboard/components/CascadeFilterComponent';
import { CASCADE_FILTER_MSG_MAP } from '@/pages/dashboard/components/CascadeFilterComponent/fieldMapper';
import { ALL_VALUE } from '@/pages/datalab/pages/ABExperiment/components/GlobalFilter';
import { getLanguage } from '@home/utils/common';

const OPERATION_INCLUDE = FilterStringOptions[0].value;
const CASCADE_PARENT_KEY = 'group_name';
const CASCADE_CHILD_KEY = 'group_code';

// 兼容分组接口与旧 options_all 的字段结构
interface IGroupOptionItem {
  code?: string;
  label_cn?: string;
  label_en?: string;
  group_name_cn?: string;
  group_name_en?: string;
  value?: string;
  text?: string;
  text_en?: string;
}

export const FilterGroupCascadeEntry = (props: EntryProps) => {
  const { column, onChange, isEn, customOptionLoader } = props;
  const [groupOptions, setGroupOptions] = useState<IGroupOptionItem[]>([]);
  const [cascadeLoading, setCascadeLoading] = useState<boolean>(true);
  const { gameCode } = useSelector((state: any) => ({
    gameCode: state.datalab?.gameCode,
  }), shallowEqual);
  const locale = getLocale();
  const operationValue = useMemo(() => {
    if (checkNullString(column.operation)) {
      return OPERATION_INCLUDE;
    }
    return column.operation ?? OPERATION_INCLUDE;
  }, [column.operation]);

  const operationOptions = useMemo(
    () =>
      FilterStringOptions.map((opt) => ({
        value: opt.value,
        text: getLanguage(isEn, opt.text_en, opt.text),
        text_en: opt.text_en ?? opt.text,
      })),
    [isEn],
  );
  const radioOptions = useMemo(
    () => [
      {
        value: ALL_VALUE,
        text: 'All',
        textCN: '全部',
      },
    ],
    [],
  );

  const handleOperationChange = useCallback(
    (value: string) => {
      if (value === column.operation) {
        return;
      }
      onChange(value, column.column_value || []);
    },
    [column, onChange],
  );

  useEffect(() => {
    let isMounted = true;
    setCascadeLoading(true);
    // 拉取分组选项：优先使用 customOptionLoader，兜底使用 column.options_all
    const fetchGroupOptions = async (): Promise<void> => {
      if (!customOptionLoader) {
        const fallback = (column.options_all ?? []).map((item) => ({
          code: item?.value,
          label_cn: item?.text ?? item?.value,
          label_en: item?.text_en ?? item?.text ?? item?.value,
          group_name_cn: column.column_display ?? '分组',
          group_name_en: column.column_display_en ?? 'Group',
        }));
        if (isMounted) {
          setCascadeLoading(false);
        }
        setGroupOptions(fallback);
        return;
      }
      try {
        const list = await customOptionLoader(column, gameCode || '');
        if (!isMounted) {
          return;
        }
        setGroupOptions((list as IGroupOptionItem[]) ?? []);
        setCascadeLoading(false);
      } catch {
        if (!isMounted) {
          return;
        }
        setGroupOptions([]);
        setCascadeLoading(false);
      }
    };
    fetchGroupOptions();
    return () => {
      isMounted = false;
    };
  }, [column, gameCode, customOptionLoader]);

  // 将接口返回的一维列表按 group_name 聚合成 NewRegionSelect 的二级结构
  const regionSelectOptions = useMemo((): OptionsItemType[] => {
    const groupMap = new Map<string, OptionsItemType>();
    groupOptions.forEach((item) => {
      const groupValue = item.group_name_en || item.group_name_cn || 'default';
      const groupText = item.group_name_en || item.group_name_cn || 'Group';
      const groupTextCN = item.group_name_cn || item.group_name_en || '分组';
      const childValue = item.code || item.value || '';
      if (!childValue) {
        return;
      }
      const childText = item.label_en || item.label_cn || item.text || childValue;
      const childTextCN = item.label_cn || item.label_en || item.text || childValue;
      if (!groupMap.has(groupValue)) {
        groupMap.set(groupValue, {
          value: groupValue,
          text: groupText,
          textCN: groupTextCN,
          children: [],
        });
      }
      const groupItem = groupMap.get(groupValue);
      if (!groupItem) {
        return;
      }
      const children = groupItem.children ?? [];
      const hasExists = children.some((child) => child?.value === childValue);
      if (hasExists) {
        return;
      }
      children.push({
        value: childValue,
        text: childText,
        textCN: childTextCN,
      });
      groupItem.children = children;
    });
    return Array.from(groupMap.values());
  }, [groupOptions]);

  // 将筛选器存储值(column.column_value)映射为级联组件 value 结构
  const cascadeValue = useMemo(() => {
    const raw = (column.column_value ?? []).filter((item) => {
      return typeof item === 'string' && item;
    });
    // 空表示「全部」，需传 ALL_VALUE，NewRegionSelect 才走单选「全部」态
    const selectedMarkets =
      raw.length === 0 ? [ALL_VALUE] : raw;
    const regions = regionSelectOptions
      .filter((groupItem) => {
        const children = groupItem.children ?? [];
        return children.some((child) => selectedMarkets.includes(child.value || ''));
      })
      .map((item) => item.value || '');
    return {
      [CASCADE_PARENT_KEY]: regions,
      [CASCADE_CHILD_KEY]: selectedMarkets,
      type: CASCADE_CHILD_KEY,
    };
  }, [column.column_value, regionSelectOptions]);

  const handleCascadeChange = useCallback(
    (_: string, nextValue: any) => {
      if (!nextValue) {
        onChange(operationValue, []);
        return;
      }
      const nextMarkets = Array.isArray(nextValue?.[CASCADE_CHILD_KEY])
        ? nextValue[CASCADE_CHILD_KEY]
        : [];
      // 仅持久化二级 code 列表，保持与筛选器协议一致
      onChange(operationValue, nextMarkets);
    },
    [onChange, operationValue],
  );

  return (
    <>
      <Select
        className="fs-entry-string-operation"
        appearance="button"
        options={operationOptions}
        value={operationValue}
        onChange={handleOperationChange}
      />
      <CascadeFilterComponent
        className="fs-entry-string-values"
        cascadeBoxClassName="dbl-filter-group-cascade-box"
        rightListWidth={559}
        filterKey={column.column_name || CASCADE_CHILD_KEY}
        value={cascadeValue}
        options={regionSelectOptions as any}
        onChange={handleCascadeChange}
        locale={locale}
        name={getLanguage(isEn, column.column_display_en, column.column_display)}
        allInfo={getLanguage(
          isEn,
          CASCADE_FILTER_MSG_MAP.campaign.all_info_en,
          CASCADE_FILTER_MSG_MAP.campaign.all_info_cn,
        )}
        lftName={getLanguage(
          isEn,
          CASCADE_FILTER_MSG_MAP.campaign.lft_name_en,
          CASCADE_FILTER_MSG_MAP.campaign.lft_name_cn,
        )}
        parentKey={CASCADE_PARENT_KEY}
        childKey={CASCADE_CHILD_KEY}
        radioOptions={radioOptions}
        loading={cascadeLoading}
      />
    </>
  );
};